import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { TicketPercent, Plus, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { Switch } from "@/components/ui/switch";
import { fmtMoney } from "@/lib/razorpay";

const errMsg = (err) => formatApiErrorDetail(err.response?.data?.detail) || err.message;
const discountLabel = (c) => (c.discount_type === "percent" ? `${c.value}% off` : `${fmtMoney(c.value * 100, "INR")} off`);

function CouponForm({ onCreated }) {
  const [code, setCode] = useState("");
  const [type, setType] = useState("percent");
  const [value, setValue] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/coupons", { code, discount_type: type, value: Number(value), description });
      toast.success(`Coupon ${code.toUpperCase()} created`);
      setCode(""); setValue(""); setDescription("");
      onCreated();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const input = "h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-medium bg-white";
  return (
    <form onSubmit={submit} data-testid="coupon-create-form" className="p-6 bg-white rounded-3xl card-tactile mb-8 grid grid-cols-1 md:grid-cols-[1fr_auto_140px_1.4fr_auto] gap-3 items-end">
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-stone mb-1">Code</label>
        <input data-testid="coupon-code-input" required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="DIWALI20" className={`${input} w-full uppercase`} />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-stone mb-1">Type</label>
        <select data-testid="coupon-type-select" value={type} onChange={(e) => setType(e.target.value)} className={`${input} w-full cursor-pointer`}>
          <option value="percent">Percentage (%)</option>
          <option value="flat">Fixed amount (₹)</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-stone mb-1">{type === "percent" ? "Percent off" : "₹ off"}</label>
        <input data-testid="coupon-value-input" required type="number" min="1" max={type === "percent" ? 90 : undefined} step="any" value={value} onChange={(e) => setValue(e.target.value)} placeholder={type === "percent" ? "20" : "500"} className={`${input} w-full`} />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-stone mb-1">Note (optional)</label>
        <input data-testid="coupon-description-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Diwali offer for siblings" className={`${input} w-full`} />
      </div>
      <button data-testid="coupon-create-button" disabled={saving} className="h-12 px-6 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover disabled:opacity-60 inline-flex items-center justify-center gap-2">
        <Plus className="w-4 h-4" /> {saving ? "Saving…" : "Create"}
      </button>
    </form>
  );
}

function CouponRow({ c, onChanged }) {
  const toggle = async (on) => {
    try {
      await api.patch(`/admin/coupons/${c.id}`, { is_active: on });
      toast.success(`${c.code} ${on ? "turned on" : "turned off"}`);
      onChanged();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete coupon ${c.code}? Past payments keep their record.`)) return;
    try { await api.delete(`/admin/coupons/${c.id}`); toast.success(`${c.code} deleted`); onChanged(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <tr className="border-b border-cloud/60 last:border-0" data-testid={`coupon-row-${c.code}`}>
      <td className="px-5 py-4 font-mono font-bold text-ink">{c.code}</td>
      <td className="px-5 py-4 font-bold text-leaf">{discountLabel(c)}{c.discount_type === "flat" && <div className="text-xs text-stone font-normal">India (INR) only</div>}</td>
      <td className="px-5 py-4 text-stone">{c.description || "—"}</td>
      <td className="px-5 py-4 tabular-nums text-ink" data-testid={`coupon-uses-${c.code}`}>{c.uses_count || 0}</td>
      <td className="px-5 py-4">
        <div className="flex items-center gap-2">
          <Switch data-testid={`coupon-toggle-${c.code}`} checked={!!c.is_active} onCheckedChange={toggle} />
          <span className={`text-xs font-bold ${c.is_active ? "text-leaf" : "text-stone"}`}>{c.is_active ? "On" : "Off"}</span>
        </div>
      </td>
      <td className="px-5 py-4 text-right">
        <button data-testid={`coupon-delete-${c.code}`} onClick={remove} className="p-2 rounded-full hover:bg-watermelon-light text-watermelon" title="Delete coupon"><Trash2 className="w-4 h-4" /></button>
      </td>
    </tr>
  );
}

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const { data } = await api.get("/admin/coupons"); setCoupons(data || []); } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <AppLayout variant="admin">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="admin-coupons-page">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-leaf/15 text-leaf font-bold text-sm mb-2">
          <TicketPercent className="w-4 h-4" /> Coupons
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-ink">Discount coupons</h1>
        <p className="text-lg text-stone mt-1 mb-8">Create codes for percentage or fixed discounts. Turn them on or off anytime — discounts apply every month on auto-debit. Max 90% off.</p>
        <CouponForm onCreated={load} />
        {loading ? (
          <div className="text-stone">Loading…</div>
        ) : coupons.length === 0 ? (
          <div className="p-10 bg-white rounded-3xl card-tactile text-center" data-testid="coupons-empty">
            <TicketPercent className="w-10 h-10 text-pebble mx-auto mb-3" />
            <div className="font-heading text-lg font-bold text-ink">No coupons yet</div>
            <div className="text-stone text-sm">Create your first code above.</div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl card-tactile overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="coupons-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-stone border-b border-cloud">
                    <th className="px-5 py-4">Code</th><th className="px-5 py-4">Discount</th><th className="px-5 py-4">Note</th>
                    <th className="px-5 py-4">Times used</th><th className="px-5 py-4">Status</th><th className="px-5 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>{coupons.map((c) => <CouponRow key={c.id} c={c} onChanged={load} />)}</tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
